import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Sparkles, Send, Layers, Grid3x3, LineChart,
  Bell, Table, Smartphone, Monitor, Tablet, ShieldCheck,
  Activity, Microscope, Scan, ClipboardList, Users
} from "lucide-react";
import { ViewBoxFrame, ViewBoxMark } from "@/components/ViewBox";

/* ────────────────────────────  HERO VISUAL — Idea → ViewBox → App  ──────────────────────────── */
function HeroViewBoxVisual() {
  return (
    <div className="relative grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.2fr)] items-center gap-4 lg:gap-6">
      {/* Left: idea bubble */}
      <div className="space-y-2 z-10">
        <div className="rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-sm">
          <p className="text-[10px] font-mono uppercase text-slate-500">clinician · idea</p>
          <p className="mt-1 text-sm text-slate-800 leading-snug">
            "Build me an app that tracks incidental imaging findings and pings the right team."
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 pl-1 text-[11px] font-mono text-emerald-800">
          <Sparkles className="h-3 w-3" /> natural language in
        </div>
      </div>

      {/* Streaming particles */}
      <div className="relative h-14 w-8 hidden md:block">
        {[0, 1, 2, 3].map((i) => (
          <span key={i}
            className="vb-idea-stream absolute left-0 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.9)]"
            style={{ animationDelay: `${i * 0.6}s` }} />
        ))}
      </div>

      {/* Right: ViewBox producing an app */}
      <ViewBoxFrame tone="dark" className="p-4">
        <div className="text-[10px] font-mono uppercase text-emerald-300/80 flex items-center gap-1.5">
          <ViewBoxMark size={12} /> viewbox · generating
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-6 w-6 rounded bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center">
            <Scan className="h-3 w-3 text-emerald-300" />
          </div>
          <div>
            <p className="font-display font-semibold text-sm text-white">Incidental Findings Tracker</p>
            <p className="text-[10px] text-slate-400">Radiology · Clinical Team Alerts</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="vb-assemble rounded-lg bg-white/5 border border-white/10 p-2" style={{ animationDelay: '.1s' }}>
            <p className="text-[9px] uppercase text-emerald-300/80 font-mono">Pending Reviews</p>
            <p className="font-display font-bold text-white text-lg leading-tight">14</p>
          </div>
          <div className="vb-assemble rounded-lg bg-white/5 border border-white/10 p-2" style={{ animationDelay: '.25s' }}>
            <p className="text-[9px] uppercase text-emerald-300/80 font-mono">Escalated</p>
            <p className="font-display font-bold text-white text-lg leading-tight">3</p>
          </div>
          <div className="vb-assemble col-span-2 rounded-lg bg-white/5 border border-white/10 p-2" style={{ animationDelay: '.4s' }}>
            <div className="flex items-center justify-between text-[10px] text-slate-300">
              <span>Findings queue</span><span className="text-emerald-300">live</span>
            </div>
            <div className="mt-1 space-y-1">
              {["Pulmonary nodule · 8mm", "Renal cyst · Bosniak II", "Adrenal incidentaloma"].map((r, i) => (
                <div key={r} className="flex items-center justify-between rounded bg-slate-800/60 px-2 py-1 text-[11px] text-slate-200">
                  <span>{r}</span><span className="text-emerald-300 font-mono">notify</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ViewBoxFrame>
    </div>
  );
}

/* ────────────────────────────  Section 4 — Build with AI  ──────────────────────────── */
function AssemblyVisual() {
  const blocks = [
    { Icon: Grid3x3, label: "Dashboard" },
    { Icon: Table, label: "Patient Table" },
    { Icon: LineChart, label: "Analytics" },
    { Icon: Layers, label: "Workflow" },
    { Icon: Bell, label: "Notifications" },
    { Icon: Smartphone, label: "Mobile View" },
  ];
  return (
    <ViewBoxFrame className="p-6 bg-white">
      <div className="grid grid-cols-3 gap-3">
        {blocks.map((b, i) => (
          <div
            key={b.label}
            className="vb-assemble rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-2 hover:border-emerald-400 transition-colors"
            style={{ animationDelay: `${i * 0.12}s` }}
          >
            <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center">
              <b.Icon className="h-4 w-4" />
            </div>
            <p className="text-xs font-mono text-slate-500 uppercase">{b.label}</p>
            <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden">
              <div className="h-full w-2/3 bg-emerald-500 vb-shine" />
            </div>
          </div>
        ))}
      </div>
    </ViewBoxFrame>
  );
}

/* ────────────────────────────  Section 3 — Conversational visual  ──────────────────────────── */
function ChatVisual() {
  return (
    <ViewBoxFrame tone="dark" className="p-5">
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <div className="h-7 w-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-white">DR</div>
          <div className="rounded-xl rounded-tl-none bg-slate-800 text-slate-100 px-3 py-2 text-sm max-w-[80%]">
            Build me a dashboard for tracking oncology patients across chemotherapy cycles.
          </div>
        </div>
        <div className="flex items-start gap-2 justify-end">
          <div className="rounded-xl rounded-tr-none bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 px-3 py-2 text-sm max-w-[80%]">
            <span className="text-[10px] font-mono uppercase text-emerald-300/90 block mb-1">viewbox ai</span>
            Generating an Oncology Cohort dashboard with cycle timelines, toxicity flags, and a care-team roster.
            <span className="mf-typing" />
          </div>
          <div className="h-7 w-7 rounded-lg bg-slate-950 border border-emerald-400/50 flex items-center justify-center">
            <ViewBoxMark size={14} />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {["Patients", "Cycles", "Toxicity"].map((k, i) => (
            <div key={k} className="vb-assemble rounded-lg bg-white/5 border border-white/10 p-2" style={{ animationDelay: `${0.4 + i * 0.15}s` }}>
              <p className="text-[9px] uppercase text-emerald-300/80 font-mono">{k}</p>
              <p className="font-display font-bold text-white text-base leading-tight">{["128", "94", "12"][i]}</p>
            </div>
          ))}
        </div>
      </div>
    </ViewBoxFrame>
  );
}

/* ────────────────────────────  Section 5 — Built for Healthcare data flows  ──────────────────────────── */
function DataFlowsVisual() {
  const sources = [
    { Icon: Scan, label: "Medical Imaging" },
    { Icon: ClipboardList, label: "Clinical Notes" },
    { Icon: Microscope, label: "Lab Results" },
    { Icon: Activity, label: "Vitals" },
    { Icon: Users, label: "Patient Workflows" },
    { Icon: LineChart, label: "Analytics" },
  ];
  return (
    <div className="grid md:grid-cols-2 gap-6 items-center">
      <div className="grid grid-cols-2 gap-3">
        {sources.map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center">
              <s.Icon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase text-slate-500">source</p>
              <p className="text-sm font-semibold text-slate-800">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
      <ViewBoxFrame tone="dark" className="p-6">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-emerald-300/80">
          <ViewBoxMark size={12} /> viewbox · unified
        </div>
        <div className="mt-3 space-y-2">
          {["Cohort dashboards", "Care coordination", "Alerts & escalation", "Clinical analytics"].map((m, i) => (
            <div key={m}
              className="vb-assemble flex items-center justify-between rounded-lg bg-white/5 border border-white/10 px-3 py-2"
              style={{ animationDelay: `${i * 0.1}s` }}>
              <span className="text-sm text-slate-100">{m}</span>
              <span className="text-[10px] font-mono text-emerald-300">module ready</span>
            </div>
          ))}
        </div>
      </ViewBoxFrame>
    </div>
  );
}

/* ────────────────────────────  Section 6 — Launch on every device  ──────────────────────────── */
function LaunchVisual() {
  return (
    <div className="relative flex items-end justify-center gap-4 lg:gap-6">
      {/* Desktop */}
      <div className="relative">
        <div className="h-56 w-96 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="h-6 border-b border-slate-100 flex items-center gap-1 px-2 bg-slate-50">
            <span className="h-2 w-2 rounded-full bg-slate-300"/>
            <span className="h-2 w-2 rounded-full bg-slate-300"/>
            <span className="h-2 w-2 rounded-full bg-slate-300"/>
          </div>
          <div className="p-3 space-y-2">
            <div className="h-6 w-1/3 rounded bg-emerald-100" />
            <div className="grid grid-cols-3 gap-2">
              {[1,2,3].map(i => <div key={i} className="h-14 rounded bg-slate-100" />)}
            </div>
            <div className="h-16 rounded bg-slate-100" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-1 mt-2 text-[10px] font-mono text-slate-500"><Monitor className="h-3 w-3" />desktop</div>
      </div>
      {/* Tablet */}
      <div className="relative">
        <div className="h-52 w-40 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="p-2 space-y-2">
            <div className="h-5 w-2/3 rounded bg-emerald-100" />
            <div className="h-10 rounded bg-slate-100" />
            <div className="h-10 rounded bg-slate-100" />
            <div className="h-10 rounded bg-slate-100" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-1 mt-2 text-[10px] font-mono text-slate-500"><Tablet className="h-3 w-3" />tablet</div>
      </div>
      {/* Mobile */}
      <div className="relative">
        <div className="h-44 w-24 rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden">
          <div className="p-2 space-y-1.5">
            <div className="h-3 w-2/3 rounded bg-emerald-100" />
            <div className="h-7 rounded bg-slate-100" />
            <div className="h-7 rounded bg-slate-100" />
            <div className="h-7 rounded bg-slate-100" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-1 mt-2 text-[10px] font-mono text-slate-500"><Smartphone className="h-3 w-3" />mobile</div>
      </div>
    </div>
  );
}

/* ────────────────────────────  Landing page  ──────────────────────────── */
export default function Landing({ onOpenAuth }) {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden mf-radial" data-testid="landing-hero-section">
        <div className="absolute inset-0 mf-grid-bg opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/70 backdrop-blur px-3 py-1 text-xs font-medium text-emerald-800 mb-6" data-testid="hero-eyebrow">
                <span className="pulse-dot" />
                theviewbox.ai · healthcare's AI builder
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
                Imagine it.<br/>
                Describe it.<br/>
                <span className="text-emerald-800">Build it.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                ViewBox AI turns healthcare ideas into working applications and websites —
                without requiring you to be a software developer.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button size="lg" onClick={() => onOpenAuth("register")}
                  className="bg-emerald-900 hover:bg-emerald-800 text-white h-12 px-6"
                  data-testid="btn-hero-start">
                  Start Building <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="#how" className="inline-flex">
                  <Button variant="outline" size="lg" className="h-12 px-6 border-slate-300" data-testid="btn-hero-how">
                    See how it works
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-sky-600" /> Healthcare-native scaffolds</div>
                <div className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-emerald-700" /> 3 frontier AI models</div>
                <div className="flex items-center gap-1.5"><ViewBoxMark size={14} glow={false}/> Idea → App in minutes</div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <HeroViewBoxVisual />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Healthcare expertise should be enough */}
      <section className="py-20 lg:py-28 bg-white border-y border-slate-200" id="how">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Healthcare expertise should be enough</p>
          <h2 className="mt-3 font-display text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">
            You already know what needs to be built.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Clinicians understand the workflows patients need. Researchers know what the data has to do.
            Historically, translating that into working software meant engineers, sprint plans, and long timelines.
            ViewBox AI closes that gap — describe the workflow you already know, and the app materializes.
          </p>
        </div>
      </section>

      {/* SECTION 3 — Describe your idea */}
      <section className="py-20 lg:py-28 bg-emerald-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Describe your idea</p>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-slate-900">
              Start with natural language. Not code.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Type the workflow you'd draw on a whiteboard. Pick the AI model you trust. ViewBox turns your
              prompt into a real product — screens, data model, and API contracts — in front of your eyes.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Send className="h-4 w-4 text-emerald-700 mt-0.5" /> Prompt in plain English or clinical shorthand</li>
              <li className="flex items-start gap-2"><Sparkles className="h-4 w-4 text-emerald-700 mt-0.5" /> Choose GPT 5.6, Claude Sonnet 5 or Gemini 3.1 Pro</li>
              <li className="flex items-start gap-2"><Layers className="h-4 w-4 text-emerald-700 mt-0.5" /> Iterate: describe → generate → review → refine</li>
            </ul>
          </div>
          <ChatVisual />
        </div>
      </section>

      {/* SECTION 4 — Build with AI */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1"><AssemblyVisual /></div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Build visually</p>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-slate-900">
              Watch software assemble itself inside the ViewBox.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Dashboards, forms, charts, workflows, tables, mobile views, navigation, notifications —
              modular pieces snap into place while you refine the idea.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Built for Healthcare */}
      <section className="py-20 lg:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Built for healthcare</p>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-slate-900">
              Not a generic builder. Designed around clinical work.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Clinical workflows, healthcare data patterns, medical terminology, structured collaboration,
              privacy-first defaults — baked in, so you don't have to reinvent the wheel each time.
            </p>
          </div>
          <div className="mt-10">
            <DataFlowsVisual />
          </div>
        </div>
      </section>

      {/* SECTION 6 — From Idea to Application */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">From idea to application</p>
          <h2 className="mt-3 font-display text-3xl lg:text-5xl font-bold text-slate-900">
            Your expertise. Your idea. Your application.
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto">Ready for desktop, tablet and mobile — the moment you say it's ready.</p>
          <div className="mt-14"><LaunchVisual /></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28 bg-slate-950 text-white relative overflow-hidden" data-testid="landing-cta-section">
        <div className="absolute inset-0 opacity-30" style={{
          background:
            "radial-gradient(600px 200px at 20% 50%, rgba(16,185,129,0.25), transparent 60%)," +
            "radial-gradient(500px 200px at 80% 50%, rgba(14,165,233,0.20), transparent 60%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight">
            What would you build for healthcare?
          </h2>
          <p className="mt-4 text-slate-300 text-lg max-w-2xl mx-auto">
            Doctors, developers, clinical ops — bring your idea, pick your model, and ship.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => onOpenAuth("register")}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-7 font-semibold"
              data-testid="btn-cta-start">
              Build It With ViewBox AI <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/marketplace">
              <Button variant="outline" size="lg" className="border-slate-600 text-white bg-transparent hover:bg-slate-800 h-12 px-6" data-testid="btn-cta-browse">
                Browse the marketplace
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <ViewBoxMark size={18} />
            <span>© 2026 ViewBox AI · theviewbox.ai</span>
          </div>
          <span className="font-mono text-xs">Imagine it. Describe it. Build it.</span>
        </div>
      </footer>
    </div>
  );
}
